/**
 * CodeCorrectionRules.jsx
 *
 * Editor for ner_code_corrections.yaml. Split out of the original
 * RuleEditorPage.jsx ("Code Corrections" tab). Body layout (3 columns: rule
 * list / form / YAML preview) preserves the original aesthetic; the outer
 * shell (Sider + Header + Content with gradient background, breadcrumb,
 * Lock + Save buttons) follows the same convention as SectionDefinitionsEditor,
 * ChronicConditionsConfig, and FocusedConditionsConfig.
 *
 * Page lands locked (read-only). Click Lock → unlock → form fields and
 * scope/priority controls become editable. Save is a placeholder (no
 * persistence). All edits are in-memory.
 *
 * Loads from public/rules/ner_code_corrections.yaml (served at
 * /rules/ner_code_corrections.yaml).
 *
 * Dependency: js-yaml
 *
 * NOTE: `YAMLHighlight` and `ResizeHandle` are duplicated between this file
 * and CorroborationRules.jsx. Easy to extract into a shared helpers module
 * later if desired.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Select, InputNumber,
  Spin, Alert, Tag, message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const NER_RULES_URL = '/rules/ner_code_corrections.yaml';

const EDITABLE_ROLES = ['hcc_reviewer'];



// ─── Design tokens (preserved from original RuleEditorPage) ─────────────
const C = {
  brand:        '#2a4a7c',
  brandDark:    '#162349',
  brandLight:   '#EDE9FE',
  border:       '#E5E7EB',
  encColor:     '#0369A1', encBg: '#E0F2FE',
  chartColor:   '#162349', chartBg: '#F5F3FF',
};

// ─── YAML preview (syntax highlighter) ──────────────────────────────────
const YAMLHighlight = ({ code }) => {
  if (!code) return null;
  const colorLine = (line) => {
    if (/^\s*#/.test(line)) return <span style={{ color: '#9CA3AF' }}>{line}</span>;
    const keyMatch = line.match(/^(\s*-?\s*)([^:]+)(:)(.*)/);
    if (keyMatch) {
      const [, indent, key, colon, rest] = keyMatch;
      const keyColor = /^(id|scope|priority|reason|type|value|code|confidence)$/.test(key.trim())
        ? '#7C3AED' : '#1D4ED8';
      const restColor = /^\s*(true|false)/.test(rest) ? '#D97706'
        : /^\s*\d/.test(rest) ? '#0369A1' : '#15803D';
      return (
        <>
          <span>{indent}</span>
          <span style={{ color: keyColor, fontWeight: 600 }}>{key}</span>
          <span style={{ color: '#6B7280' }}>{colon}</span>
          <span style={{ color: restColor }}>{rest}</span>
        </>
      );
    }
    return <span style={{ color: '#374151' }}>{line}</span>;
  };
  return (
    <pre style={{
      fontFamily: 'monospace', fontSize: 13, lineHeight: 1.65,
      background: '#FAFAFA', border: `1px solid ${C.border}`,
      borderRadius: 6, padding: '10px 14px',
      overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      margin: 0, flex: 1,
    }}>
      {code.split('\n').map((line, i) => <div key={i}>{colorLine(line)}</div>)}
    </pre>
  );
};

// ─── Resize handle ──────────────────────────────────────────────────────
const ResizeHandle = ({ onDrag }) => (
  <div
    onMouseDown={onDrag}
    style={{
      width: 6, flexShrink: 0, cursor: 'col-resize', background: C.border,
      position: 'relative', zIndex: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => (e.currentTarget.style.background = C.brand + '55')}
    onMouseLeave={e => (e.currentTarget.style.background = C.border)}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none' }}>
      {[0,1,2,3,4].map(i => <div key={i} style={{ width: 2, height: 2, borderRadius: '50%', background: '#9CA3AF' }} />)}
    </div>
  </div>
);

// ─── Panel section header strip ─────────────────────────────────────────
const PanelHeader = ({ label, sub, right }) => (
  <div style={{
    padding: '7px 12px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  }}>
    <div>
      <Text style={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</Text>
      {sub && <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>{sub}</Text>}
    </div>
    {right}
  </div>
);

// ─── NER Rule List (left panel) ─────────────────────────────────────────
const NERRuleList = ({ rules, selectedId, onSelect }) => {
  const grouped = useMemo(() => ({
    encounter: (rules || []).filter(r => r.scope === 'encounter').sort((a, b) => a.priority - b.priority),
    chart:     (rules || []).filter(r => r.scope === 'chart').sort((a, b) => a.priority - b.priority),
  }), [rules]);

  const renderGroup = (label, color, bg, list) => (
    <div key={label}>
      <div style={{ padding: '5px 12px 3px', background: bg, borderBottom: `1px solid ${C.border}` }}>
        <Text style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {label} ({list.length})
        </Text>
      </div>
      {list.map(r => {
        const isActive = r.id === selectedId;
        return (
          <div
            key={r.id}
            onClick={() => onSelect(r)}
            style={{
              padding: '9px 12px', cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              borderLeft: `3px solid ${isActive ? C.brand : 'transparent'}`,
              background: isActive ? C.brandLight : 'transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: isActive ? C.brand : '#E5E7EB',
                color: isActive ? '#fff' : '#6B7280', fontFamily: 'monospace',
              }}>{r.priority}</span>
              <Text style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: '#111827', lineHeight: '15px' }}>
                {r.id}
              </Text>
            </div>
            <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginTop: 2, lineHeight: '14px' }}>
              {(r.reason || '').slice(0, 55)}{(r.reason || '').length > 55 ? '…' : ''}
            </Text>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {renderGroup('Encounter Scope', C.encColor, C.encBg + '88', grouped.encounter)}
      {renderGroup('Chart Scope',     C.chartColor, '#D4E7F0',     grouped.chart)}
    </div>
  );
};

// ─── NER Rule Form (middle panel) ───────────────────────────────────────
const NERRuleForm = ({ rule, onChange, editMode }) => {
  if (!rule) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Text style={{ color: '#9CA3AF', fontSize: 15 }}>Select a rule to edit</Text>
    </div>
  );

  const labelStyle = {
    fontSize: 13, fontWeight: 700, color: '#374151',
    display: 'block', marginBottom: 4,
  };

  return (
    <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
      {/* id + scope + priority */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 2 }}>
          <Text style={labelStyle}>Rule ID</Text>
          <Input value={rule.id} readOnly
            style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, background: '#F9FAFB' }} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <Text style={labelStyle}>Scope</Text>
          <Select
            value={rule.scope}
            disabled={!editMode}
            onChange={val => onChange({ ...rule, scope: val })}
            style={{ width: '100%', fontSize: 13 }}
          >
            <Option value="encounter"><Tag color="blue" style={{ fontSize: 12 }}>encounter</Tag></Option>
            <Option value="chart"><Tag color="purple" style={{ fontSize: 12 }}>chart</Tag></Option>
          </Select>
        </div>
        <div style={{ flex: '0 0 90px' }}>
          <Text style={labelStyle}>Priority</Text>
          <InputNumber
            value={rule.priority}
            min={1} max={999}
            disabled={!editMode}
            onChange={val => onChange({ ...rule, priority: val })}
            style={{ width: '100%', fontSize: 13 }}
          />
        </div>
      </div>

      {/* Trigger */}
      <div style={{ marginBottom: 14 }}>
        <Text style={labelStyle}>Trigger</Text>
        <TextArea
          value={rule.trigger}
          onChange={e => onChange({ ...rule, trigger: e.target.value })}
          readOnly={!editMode}
          rows={6}
          placeholder="When does this rule apply? (plain English — used as LLM screening prompt)"
          style={{ fontSize: 14 }}
        />
      </div>

      {/* Correction */}
      <div style={{ marginBottom: 14 }}>
        <Text style={labelStyle}>Correction</Text>
        <TextArea
          value={rule.correction}
          onChange={e => onChange({ ...rule, correction: e.target.value })}
          readOnly={!editMode}
          rows={7}
          placeholder="What should the LLM do? (plain English — used as LLM resolution prompt)"
          style={{ fontSize: 14 }}
        />
      </div>

      {/* Reason */}
      <div style={{ marginBottom: 14 }}>
        <Text style={labelStyle}>Reason</Text>
        <Input
          value={rule.reason}
          onChange={e => onChange({ ...rule, reason: e.target.value })}
          readOnly={!editMode}
          placeholder="Short audit label stored in trace"
          style={{ fontSize: 14, fontFamily: 'monospace' }}
        />
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────
const CodeCorrectionRules = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [nerData, setNerData]       = useState(null);
  const [selNerRule, setSelNerRule] = useState(null);
  const [editMode, setEditMode]     = useState(false); // false = locked

  // Resizable list-panel width
  const [listW, setListW] = useState(280);

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Load YAML ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(NER_RULES_URL)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(text => {
        if (cancelled) return;
        const parsed = yaml.load(text);
        setNerData(parsed);
        const first = parsed?.rules?.[0];
        if (first) setSelNerRule(first);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load ner_code_corrections.yaml:', err);
        setLoadError(err.message || 'Unknown error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Resize drag ──────────────────────────────────────────────────
  const startDrag = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX, base = listW;
    const onMove = me => setListW(Math.max(200, base + (me.clientX - startX)));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [listW]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);
  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const handleRuleChange = (updated) => {
    setSelNerRule(updated);
    setNerData(prev => ({
      ...prev,
      rules: (prev?.rules || []).map(r => r.id === updated.id ? updated : r),
    }));
  };

  // ── YAML preview ─────────────────────────────────────────────────
  const yamlPreview = useMemo(() => {
    if (!selNerRule) return '';
    try {
      return yaml.dump({
        id: selNerRule.id,
        scope: selNerRule.scope,
        priority: selNerRule.priority,
        trigger: selNerRule.trigger,
        correction: selNerRule.correction,
        reason: selNerRule.reason,
      }, { lineWidth: 56, indent: 2 });
    } catch { return ''; }
  }, [selNerRule]);

  // ── Stats for the strip ──────────────────────────────────────────
  const stats = useMemo(() => {
    const rules = nerData?.rules || [];
    return {
      total: rules.length,
      encounter: rules.filter(r => r.scope === 'encounter').length,
      chart: rules.filter(r => r.scope === 'chart').length,
    };
  }, [nerData]);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider ──────────────────────────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'fixed',
          left: 0, top: 0, bottom: 0, zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column', justifyContent: 'space-between',
          height: '100%', alignItems: 'center', padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white', fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Code Corrections
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>

        </Header>

        {/* ── Content ───────────────────────────────────────────── */}
        <Content style={{
          padding: 0,
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex', flexDirection: 'column',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading code correction rules…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={<span>Could not load <code>{NER_RULES_URL}</code>. ({loadError})</span>}
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && nerData && (
            <>
              {/* Stats strip */}
              <div style={{
                padding: '10px 20px',
                background: 'rgba(255,255,255,0.6)',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 24,
                flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.brandDark, lineHeight: '20px' }}>{stats.total}</div>
                  <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rules</div>
                </div>
                <div style={{ flex: 1 }} />
                <Text style={{ fontSize: 12, color: '#6B7280' }}>
                  {nerData.namespace} · v{nerData.version}
                </Text>
              </div>

              {/* Editor body: 3 columns */}
              <div style={{
                flex: 1, display: 'flex', overflow: 'hidden',
                background: 'white',
                margin: 16,
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}>

                {/* P1: Rule List */}
                <div style={{
                  width: listW, flexShrink: 0,
                  display: 'flex', flexDirection: 'column',
                  background: '#FAFAFA',
                  overflow: 'hidden',
                  borderTopLeftRadius: 8, borderBottomLeftRadius: 8,
                }}>
                  <PanelHeader
                    label="Rules"
                    sub={`${nerData?.namespace} · v${nerData?.version}`}
                  />
                  <NERRuleList
                    rules={nerData?.rules || []}
                    selectedId={selNerRule?.id}
                    onSelect={r => setSelNerRule(r)}
                  />
                </div>

                <ResizeHandle onDrag={startDrag} />

                {/* P2: form + YAML side by side */}
                <div style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden', background: '#fff',
                  borderTopRightRadius: 8, borderBottomRightRadius: 8,
                }}>
                  <PanelHeader
                    label={selNerRule?.id || 'Rule Editor'}
                    sub={selNerRule
                      ? `scope: ${selNerRule.scope} · priority: ${selNerRule.priority}`
                      : 'Select a rule from the list'}
                    right={selNerRule && (
                      <span style={{
                        fontSize: 13, padding: '2px 10px', borderRadius: 4, fontWeight: 700,
                        background: selNerRule.scope === 'encounter' ? C.encBg : '#F5F3FF',
                        color:      selNerRule.scope === 'encounter' ? C.encColor : C.chartColor,
                        border:     `1px solid ${selNerRule.scope === 'encounter' ? '#BAE6FD' : '#DDD6FE'}`,
                      }}>
                        {selNerRule.scope}
                      </span>
                    )}
                  />
                  <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                    {/* form */}
                    <div style={{
                      flex: 1, minWidth: 0, overflow: 'hidden',
                      display: 'flex', flexDirection: 'column', userSelect: 'text',
                    }}>
                      <NERRuleForm
                        rule={selNerRule}
                        onChange={handleRuleChange}
                        editMode={editMode}
                      />
                    </div>

                    {/* yaml preview */}
                    <div style={{
                      width: 340, flexShrink: 0,
                      borderLeft: `1px solid ${C.border}`,
                      display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    }}>
                      <div style={{
                        padding: '7px 12px', borderBottom: `1px solid ${C.border}`,
                        background: '#F5F3FF', flexShrink: 0,
                      }}>
                        <Text style={{ fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          YAML Preview
                        </Text>
                        <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>
                          Live · updates as you edit
                        </Text>
                      </div>
                      <div style={{ flex: 1, overflowY: 'auto', padding: 12, userSelect: 'text' }}>
                        <YAMLHighlight code={yamlPreview} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

export default CodeCorrectionRules;